import { Checkbox, Form, Select } from "antd";
import { ReactElement, useEffect, useState } from "react";

import "./edit-financial.scss";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import { AxiosResponse } from "axios";
import toast from "react-hot-toast";
import CustomInput from "src/components/custom-input/custom-input.component";
import TextArea from "antd/es/input/TextArea";
import { LOADING } from "src/store/actions";
import instance from "src/store/axios";
import Label from "src/components/label/label.component";
import { getStandingDataOptions } from "src/utils/helper/functions";

const EditFinancial: React.FC<{
  onSuccess: () => void;
  accountDetails: any;
}> = ({ onSuccess, accountDetails }): ReactElement => {
  const { accountId, activeKyc } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isApproveCase = queryParams.get("approve");
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [submitForm, setSubmitForm] = useState<boolean>(false);

  const [standingData, setStandingData] = useState<any>();

  useEffect(() => {
    async function prepareData() {
      dispatch({ type: LOADING, payload: true });
      await getStandindData();
      dispatch({ type: LOADING, payload: false });
    }

    prepareData();
  }, []);

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmitForm(true);
      },
      () => {
        setSubmitForm(false);
      }
    );
  }, [values]);

  const getStandindData = async (): Promise<void> => {
    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/option/standing-data`
      );
      setStandingData(response.data.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/home/new-kyc/${accountDetails?.id}/${accountDetails?.pendingKycId}`
      );
    }
  };

  const getSourceOfIncome = () => {
    const array = [];
    if (accountDetails?.accountInfo[0]?.sourceOfIncome1) array.push(275);
    if (accountDetails?.accountInfo[0]?.sourceOfIncome2) array.push(276);
    if (accountDetails?.accountInfo[0]?.sourceOfIncome3) array.push(377);
    if (accountDetails?.accountInfo[0]?.sourceOfIncomeOther) array.push(-1);
    return array;
  };

  const getSourceOfWealth = () => {
    const array = [];

    if (accountDetails?.accountInfo[0]?.sourceOfWealth1)
      array.push("sourceOfWealth1");
    if (accountDetails?.accountInfo[0]?.sourceOfWealth2)
      array.push("sourceOfWealth2");
    if (accountDetails?.accountInfo[0]?.sourceOfWealth3)
      array.push("sourceOfWealth3");
    if (accountDetails?.accountInfo[0]?.sourceOfWealth4)
      array.push("sourceOfWealth4");
    if (accountDetails?.accountInfo[0]?.sourceOfWealth5)
      array.push("sourceOfWealth5");
    if (accountDetails?.accountInfo[0]?.sourceOfWealth6)
      array.push("sourceOfWealth6");

    return array;
  };

  const onSubmit = async (values: any): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    if (values?.sourceOfIncome?.length) {
      if (values?.sourceOfIncome?.includes(275)) {
        values.sourceOfIncome1 = true;
        values.sourceOfIncome1AmtId = 275;
      }
      if (values?.sourceOfIncome?.includes(276)) {
        values.sourceOfIncome2 = true;
        values.sourceOfIncome2AmtId = 276;
      }
      if (values?.sourceOfIncome?.includes(377)) {
        values.sourceOfIncome3 = true;
        values.sourceOfIncome3AmtId = 377;
      }
    }

    delete values.sourceOfIncome;

    if (values.investmentObjectiveId == 59) {
      values.investmentObjectiveCapitalProtection = true;
    } else {
      values.investmentObjectiveCapitalProtection = false;
    }

    if (values.investmentObjectiveId == 57) {
      values.investmentObjectiveIncome = true;
    } else {
      values.investmentObjectiveIncome = false;
    }

    if (values.investmentObjectiveId == 58) {
      values.investmentObjectiveCapitalGrowth = true;
    } else {
      values.investmentObjectiveCapitalGrowth = false;
    }

    if (values.investmentObjectiveId == 405) {
      values.investmentObjectiveBalanced = true;
    } else {
      values.investmentObjectiveBalanced = false;
    }
    values.branch = "SA";

    delete values.investmentObjectiveId;

    delete values.sourceOfIncomeInformation;

    delete values.wealthInformation;

    values.kycId = accountDetails.accountInfo[0]?.id;
    values.expectedAmountActivity = +values.expectedAmountActivity;
    values.initialInvestmentAmount = +values.initialInvestmentAmount;

    // Set classification based on the new logic
    const classification = getClientClassification();
    values.isRetailClient = classification === "RETAIL";
    values.qualifiedProfessionalClient = classification === "QUALIFIED";

    try {
      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/financial-info/update`,
        values
      );

      toast.success("Financial information updated successfully");
      onSuccess();
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/home/new-kyc/${accountDetails?.id}/${accountDetails?.pendingKycId}`
      );
    }

    dispatch({ type: LOADING, payload: false });
  };

  const getInvestmentObjectiveValues = () => {
    const valuesArray = [];
    if (accountDetails.accountInfo[0]?.investmentObjectiveCapitalGrowth) {
      valuesArray.push(58);
    }
    if (accountDetails.accountInfo[0]?.investmentObjectiveCapitalProtection) {
      valuesArray.push(59);
    }
    if (accountDetails.accountInfo[0]?.investmentObjectiveIncome) {
      valuesArray.push(57);
    }
    if (accountDetails.accountInfo[0]?.investmentObjectiveBalanced) {
      valuesArray.push(405);
    }

    return valuesArray;
  };

  const calculateSuitabilityScore = () => {
    let score = 0;
    const scoreBreakdown: { field: string; value: any; points: number; note?: string }[] = [];

    // Educational Level (Primary =0, Intermediate = 1, High School = 2, Diploma = 3, Bachelor = 4, Master = 5, Doctorate =6)
    const educationLevel = accountDetails?.accountInfo[0]?.educationLevelId;
    let educationPoints = 0;
    let educationNote = "";
    if (educationLevel == 252) { educationPoints = 0; educationNote = "Primary"; }
    else if (educationLevel == 253) { educationPoints = 1; educationNote = "Intermediate"; }
    else if (educationLevel == 25) { educationPoints = 2; educationNote = "High School"; }
    else if (educationLevel == 30) { educationPoints = 3; educationNote = "Diploma"; }
    else if (educationLevel == 26) { educationPoints = 4; educationNote = "Bachelor"; }
    else if (educationLevel == 257) { educationPoints = 5; educationNote = "Master"; }
    else if (educationLevel == 258) { educationPoints = 5; educationNote = "Doctorate"; }
    else { educationNote = `Unknown ID (expected 71-77)`; }
    score += educationPoints;
    scoreBreakdown.push({ field: "Educational Level", value: educationLevel, points: educationPoints, note: educationNote });

    // Are you employed by a registered broker dealer or any securities exchange? (No = 0, Yes = 3)
    const wasBrokerDealerEmployee = form.getFieldValue("wasBrokerDealerEmployee");
    const brokerPoints = wasBrokerDealerEmployee ? 3 : 0;
    score += brokerPoints;
    scoreBreakdown.push({ field: "Broker Dealer Employee", value: wasBrokerDealerEmployee, points: brokerPoints });

    // Have you ever worked in the Financial Sector during the past 5 years? (No = 0, Yes = 3)
    const hasWorkedInFinancialSector = form.getFieldValue("hasWorkedInFinancialSector");
    const financialSectorPoints = hasWorkedInFinancialSector ? 3 : 0;
    score += financialSectorPoints;
    scoreBreakdown.push({ field: "Worked in Financial Sector", value: hasWorkedInFinancialSector, points: financialSectorPoints });

    // Do you have any other experience related to Financial Sector? (No = 0, Yes = 3)
    const hasOtherFinancialExperience = form.getFieldValue("hasOtherFinancialExperience");
    const otherFinancialPoints = hasOtherFinancialExperience ? 3 : 0;
    score += otherFinancialPoints;
    scoreBreakdown.push({ field: "Other Financial Experience", value: hasOtherFinancialExperience, points: otherFinancialPoints });

    // Number of Years of Investment Experience (<1 year = 1, 1-5 years = 3, More than 5 years = 5)
    const yearsInvestmentExpId = form.getFieldValue("yearsInvestmentExpId");
    let yearsExpPoints = 0;
    if (yearsInvestmentExpId == 421) yearsExpPoints = 1; // <1 year
    else if (yearsInvestmentExpId == 422) yearsExpPoints = 3; // 1-5 years
    else if (yearsInvestmentExpId == 423) yearsExpPoints = 5; // More than 5 years
    score += yearsExpPoints;
    scoreBreakdown.push({ field: "Years Investment Experience", value: yearsInvestmentExpId, points: yearsExpPoints });

    // Previous Invested Instruments (Equities = 2, Mutual Funds = 1, Margin Lending = 3, Derivatives = 4)
    const productsPreviouslyInvested = form.getFieldValue("productsPreviouslyInvested");
    let productsPoints = 0;
    if (productsPreviouslyInvested?.includes(411)) productsPoints += 1; // Mutual Funds
    if (productsPreviouslyInvested?.includes(410)) productsPoints += 2; // Equities
    if (productsPreviouslyInvested?.includes(412)) productsPoints += 3; // Margin Lending
    if (productsPreviouslyInvested?.includes(413)) productsPoints += 4; // Derivatives
    score += productsPoints;
    scoreBreakdown.push({ field: "Products Previously Invested", value: productsPreviouslyInvested, points: productsPoints });

    // Professional Certificates (No = 0, Yes = 22)
    const hasAccreditedCertifications = accountDetails?.accountInfo[0]?.hasAccreditedCertifications;
    const certPoints = hasAccreditedCertifications ? 22 : 0;
    score += certPoints;
    scoreBreakdown.push({ field: "Professional Certificates", value: hasAccreditedCertifications, points: certPoints });

    // Investment experience (None = 0, Low = 3, Medium = 6, High = 10)
    const investmentExpId = form.getFieldValue("investmentExpId");
    let invExpPoints = 0;
    if (investmentExpId == 48) invExpPoints = 0; // None
    else if (investmentExpId == 407) invExpPoints = 3; // Low
    else if (investmentExpId == 408) invExpPoints = 6; // Medium
    else if (investmentExpId == 409) invExpPoints = 10; // High
    score += invExpPoints;
    scoreBreakdown.push({ field: "Investment Experience Level", value: investmentExpId, points: invExpPoints });

    // Client appetite for risk (Low = 10, Medium = 15, High = 20)
    const riskToleranceId = form.getFieldValue("riskToleranceId");
    let riskPoints = 0;
    if (riskToleranceId == 84) riskPoints = 10; // Low
    else if (riskToleranceId == 85) riskPoints = 15; // Medium
    else if (riskToleranceId == 83) riskPoints = 20; // High
    score += riskPoints;
    scoreBreakdown.push({ field: "Risk Tolerance", value: riskToleranceId, points: riskPoints });

    // The period during which the client expects to cash out his invested money (Short Term = 3, Medium Term = 6, Long Term = 10)
    const periodExpectsCashoutId = form.getFieldValue("periodExpectsCashoutId");
    let cashoutPoints = 0;
    if (periodExpectsCashoutId == 414) cashoutPoints = 3; // Short Term
    else if (periodExpectsCashoutId == 415) cashoutPoints = 6; // Medium Term
    else if (periodExpectsCashoutId == 416) cashoutPoints = 10; // Long Term
    score += cashoutPoints;
    scoreBreakdown.push({ field: "Period Expects Cashout", value: periodExpectsCashoutId, points: cashoutPoints });

    // Investment Objective (Capital Preservation = 2, Balanced = 3, Income = 4, Growth = 5)
    const investmentObjectives = form.getFieldValue("investmentObjectiveId");
    let objectivePoints = 0;
    if (investmentObjectives == 59) objectivePoints = 2;
    else if (investmentObjectives == 405) objectivePoints = 3;
    else if (investmentObjectives == 57) objectivePoints = 4;
    else if (investmentObjectives == 58) objectivePoints = 5;
    score += objectivePoints;
    scoreBreakdown.push({ field: "Investment Objective", value: investmentObjectives, points: objectivePoints });

    // Dependence on Advisory Services (No = 4, Yes = 0)
    const hasInvestmentAdvisor = form.getFieldValue("hasInvestmentAdvisor");
    let advisorPoints = 0;
    if (hasInvestmentAdvisor === true) advisorPoints = 0;
    else if (hasInvestmentAdvisor === false) advisorPoints = 4;
    // null/undefined = 0 points (not answered yet)
    score += advisorPoints;
    scoreBreakdown.push({ field: "Has Investment Advisor", value: hasInvestmentAdvisor, points: advisorPoints });

    return score;
  };

  const getSuitabilityLevel = (score: number) => {
    if (score >= 0 && score <= 30) return "LOW";
    if (score >= 31 && score <= 70) return "MEDIUM";
    if (score >= 71 && score <= 100) return "HIGH";
    return "LOW";
  };

  const getSuitableProducts = (score: number) => {
    if (score >= 0 && score <= 30) {
      return [
        "Local Shares Trading",
        "Global Shares Trading",
        "Sukuk Trading",
        "Money Market Funds",
        "Non-Discretionary Portfolio Management (NDPM)",
      ];
    }
    if (score >= 31 && score <= 70) {
      return ["Real Estate Funds", "Discretionary Portfolio Management (DPM)"];
    }
    if (score >= 71 && score <= 100) {
      return [
        "Nomu market Trading",
        "Margin Trading",
        "Funds Private Placement",
        "Equities Funds",
      ];
    }
    return [];
  };

  const getQualifyingNetWorthAndCME1Certification = () => {
    const qualifyingNetWorthIds = [389, 115, 118, 116, 390, 114, 391, 392, 393];
    const totalWealthId = form.getFieldValue("totalWealthId");
    const hasCME1Certification = accountDetails?.accountInfo[0]?.certifications?.includes(259);
    return qualifyingNetWorthIds.includes(totalWealthId) && hasCME1Certification;
  }

  const getClientClassification = () => {
    const classificationBreakdown: { condition: string; value: any; qualifies: boolean }[] = [];

    // Condition 1: Employed by registered broker-dealer or securities exchange
    const wasBrokerDealerEmployee = form.getFieldValue("wasBrokerDealerEmployee");
    const condition1Qualifies = wasBrokerDealerEmployee === true;
    classificationBreakdown.push({
      condition: "Employed by broker-dealer/securities exchange",
      value: wasBrokerDealerEmployee,
      qualifies: condition1Qualifies,
    });

    // Condition 2: Net worth >= 600K (CME-1, ID: 259)
    // Qualifying values: "600K – 1.5M", "1.5M – 5M", "5M – 10M", "10M-50M", "More than 50M"

    const condition2Qualifies = getQualifyingNetWorthAndCME1Certification();

    // Condition 3: Has professional certificates
    const hasAccreditedCertifications = accountDetails?.accountInfo[0]?.hasAccreditedCertifications;
    const condition3Qualifies = hasAccreditedCertifications === true;
    classificationBreakdown.push({
      condition: "Has professional certificates",
      value: hasAccreditedCertifications,
      qualifies: condition3Qualifies,
    });

    // Client is QUALIFIED if ANY ONE condition is met
    const isQualified = condition1Qualifies || condition2Qualifies || condition3Qualifies;
    const classification = isQualified ? "QUALIFIED" : "RETAIL";

    return classification;
  };

  return (
    <div className="edit-financial flex flex-col mt-5">
      {accountDetails?.id && standingData && (
        <Form
          onFinish={onSubmit}
          scrollToFirstError
          className="add-edit-form flex flex-col gap-5"
          initialValues={{
            sourceOfIncome: getSourceOfIncome(),
            sourceOfIncomeOther:
              accountDetails.accountInfo[0]?.sourceOfIncomeOther,
            investmentObjectiveId: getInvestmentObjectiveValues(),
            investmentTypeId: accountDetails.accountInfo[0]?.investmentTypeId,
            annualRevenueId: accountDetails.accountInfo[0]?.annualRevenueId,
            totalWealthId: accountDetails.accountInfo[0]?.totalWealthId,
            riskToleranceId: accountDetails.accountInfo[0]?.riskToleranceId,
            investmentExpId: accountDetails.accountInfo[0]?.investmentExpId,
            expectedAmountActivity:
              +accountDetails.accountInfo[0]?.expectedAmountActivity,
            initialInvestmentAmount:
              +accountDetails.accountInfo[0]?.initialInvestmentAmount,
            yearsInvestmentExpId:
              accountDetails.accountInfo[0]?.yearsInvestmentExpId,
            hasInvestmentAdvisor:
              accountDetails.accountInfo[0]?.hasInvestmentAdvisor,
            sourceOfIncomeInformation: getSourceOfIncome(),
            wealthInformation: getSourceOfWealth(),
            sourceOfWealth1AmtId:
              accountDetails.accountInfo[0]?.sourceOfWealth1AmtId,
            sourceOfWealth2AmtId:
              accountDetails.accountInfo[0]?.sourceOfWealth2AmtId,
            sourceOfWealth3AmtId:
              accountDetails.accountInfo[0]?.sourceOfWealth3AmtId,
            sourceOfWealth4AmtId:
              accountDetails.accountInfo[0]?.sourceOfWealth4AmtId,
            sourceOfWealth5AmtId:
              accountDetails.accountInfo[0]?.sourceOfWealth5AmtId,
            sourceOfWealth6AmtId:
              accountDetails.accountInfo[0]?.sourceOfWealth6AmtId,
            sourceOfIncomeDetails:
              accountDetails?.accountInfo[0]?.sourceOfIncomeDetails,
            sourceOfWealthDetails:
              accountDetails?.accountInfo[0]?.sourceOfWealthDetails,
            productsPreviouslyInvested:
              accountDetails.accountInfo[0]?.productsPreviouslyInvested
                ?.split("|")
                ?.map((item: string) => +item.trim()),
            periodExpectsCashoutId:
              accountDetails.accountInfo[0]?.periodExpectsCashoutId,
            purposeOpeningAccountId:
              accountDetails.accountInfo[0]?.purposeOpeningAccountId,
            isListedCompanyShareholder:
              accountDetails?.accountInfo[0]?.isListedCompanyShareholder,
            hasWorkedInFinancialSector:
              accountDetails?.accountInfo[0]?.hasWorkedInFinancialSector,
            hasOtherFinancialExperience:
              accountDetails?.accountInfo[0]?.hasOtherFinancialExperience,
            wasBrokerDealerEmployee:
              accountDetails?.accountInfo[0]?.wasBrokerDealerEmployee,
            otherPurposeOfOpeningAccount:
              accountDetails?.accountInfo[0]?.otherPurposeOfOpeningAccount,
          }}
          form={form}
        >
          <div className="flex gap-3 items-center">
            <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
            <h3 className=" text-lg font-semibold">Financial information</h3>
          </div>
          <div className="fields flex justify-space-between wrap">
            <div className="field flex flex-col">
              <Label text="Source of Income" required />
              <Form.Item
                name="sourceOfIncome"
                rules={[
                  {
                    required: true,
                    message: "Please select source of income!",
                  },
                ]}
              >
                <Select
                  placeholder="Select source of income"
                  mode="multiple"
                  options={getStandingDataOptions(
                    standingData,
                    "sourceOfIncome",
                    ["sa", "gc"],
                    true
                  )}
                />
              </Form.Item>
            </div>

            {form.getFieldValue("sourceOfIncome")?.includes(-1) && (
              <div className="field flex flex-col">
                <Label text="Other source of income" required />
                <Form.Item
                  name="sourceOfIncomeOther"
                  rules={[
                    {
                      required: true,
                      message: "Please enter other source of income!",
                    },
                  ]}
                >
                  <CustomInput placeholder="Enter other source of income" />
                </Form.Item>
              </div>
            )}

            <div className="field flex flex-col">
              <Label text="Annual Income (S.A.R.)" required />
              <Form.Item
                name="annualRevenueId"
                rules={[
                  {
                    required: true,
                    message: "Please enter Annual Income (S.A.R.)!",
                  },
                ]}
              >
                <Select
                  placeholder="Select Annual Income (S.A.R.)"
                  options={getStandingDataOptions(
                    standingData,
                    "revenueRange",
                    ["sa", "gc"]
                  )}
                />
              </Form.Item>
            </div>

            <div className="field flex flex-col">
              <Label text="Approximate net worth (excluding residence) (S.A.R.)" required />
              <Form.Item
                name="totalWealthId"
                rules={[
                  {
                    required: true,
                    message: "Please enter approximate net worth!",
                  },
                ]}
              >
                <Select
                  placeholder="Enter approximate net worth"
                  options={getStandingDataOptions(standingData, "totalWealth", [
                    "sa",
                    "gc",
                  ])}
                />
              </Form.Item>
            </div>
            <div className="field flex column">
              <Label text="Investment objective" required />
              <Form.Item
                name="investmentObjectiveId"
                rules={[
                  {
                    required: true,
                    message: "Please select the Investment objective!",
                  },
                ]}
              >
                <Select
                  placeholder="Select Investment objective"
                  options={getStandingDataOptions(
                    standingData,
                    "investmentObjective",
                    ["sa", "gc"]
                  )}
                />
              </Form.Item>
            </div>

            <div className="field flex column">
              <Label text="Investment Experience" required />
              <Form.Item
                name="investmentExpId"
                rules={[
                  {
                    required: true,
                    message: "Please select Investment Experience!",
                  },
                ]}
              >
                <Select
                  placeholder="Select Investment Experience"
                  options={getStandingDataOptions(
                    standingData,
                    "investmentExperience",
                    ["sa", "gc"]
                  )}
                />
              </Form.Item>
            </div>

            <div className="field flex column">
              <Label text="Appetite for Risk" required />
              <Form.Item
                name="riskToleranceId"
                rules={[
                  {
                    required: true,
                    message: "Please select Appetite for Risk!",
                  },
                ]}
              >
                <Select
                  placeholder="Select Appetite for Risk"
                  options={getStandingDataOptions(
                    standingData,
                    "riskTolerance",
                    ["sa", "gc"]
                  )}
                />
              </Form.Item>
            </div>

            <div className="field flex column">
              <Label text="Products previously invested in" required />
              <Form.Item
                name="productsPreviouslyInvested"
                rules={[
                  {
                    required: true,
                    message: "Please select Products previously invested in!",
                  },
                ]}
              >
                <Select
                  placeholder="Select Products previously invested in"
                  mode="multiple"
                  options={getStandingDataOptions(
                    standingData,
                    "productsPreviouslyInvested",
                    ["sa", "gc"]
                  )}
                />
              </Form.Item>
            </div>

            <div className="field flex flex-col">
              <Label text="Number of years of investment experience" required />
              <Form.Item
                name="yearsInvestmentExpId"
                rules={[
                  {
                    required: true,
                    message:
                      "Please select number of years of investment experience",
                  },
                ]}
              >
                <Select
                  placeholder="Select number of years of investment experience"
                  options={getStandingDataOptions(
                    standingData,
                    "investmentExperienceYears",
                    ["sa", "gc"]
                  )}
                />
              </Form.Item>
            </div>

            <div className="field flex flex-col">
              <Label text="The period during which the client expects to cash out his invested money" required />
              <Form.Item
                name="periodExpectsCashoutId"
                rules={[
                  {
                    required: true,
                    message:
                      "Please select the period during which the client expects to cash out his invested money!",
                  },
                ]}
              >
                <Select
                  placeholder="Select the period during which the client expects to cash out his invested money"
                  options={getStandingDataOptions(
                    standingData,
                    "periodExpectsCashout",
                    ["sa", "gc"]
                  )}
                />
              </Form.Item>
            </div>

            <div className="field flex column">
              <Label text="Do you rely on an investment advisor" required />
              <Form.Item
                name="hasInvestmentAdvisor"
                rules={[
                  {
                    required: true,
                    message:
                      "Please select Do you rely on an investment advisor!",
                  },
                ]}
              >
                <Select
                  placeholder="Select Do you rely on an investment advisor"
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ]}
                />
              </Form.Item>
            </div>

            <div className="field flex flex-col">
              <Label text="Purpose of opening the account" required />
              <Form.Item
                name="purposeOpeningAccountId"
                rules={[
                  {
                    required: true,
                    message: "Please select purpose of opening the account!",
                  },
                ]}
              >
                <Select
                  placeholder="Select purpose of opening the account"
                  options={getStandingDataOptions(
                    standingData,
                    "purposeOpeningAccount",
                    ["sa", "gc"],
                    true
                  )}
                />
              </Form.Item>
            </div>

            {form.getFieldValue("purposeOpeningAccountId") == "-1" && (
              <div className="field flex flex-col">
                <Label text="Other purpose of opening the account" required />
                <Form.Item
                  name="otherPurposeOfOpeningAccount"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please enter other purpose of opening the account!",
                    },
                  ]}
                >
                  <CustomInput placeholder="Enter other purpose of opening the account" />
                </Form.Item>
              </div>
            )}

            <div className="field flex flex-col">
              <Label text="Have you been employed by a registered broker-dealer or any Securities Exchange in the past 3 years?" required />
              <Form.Item
                name="wasBrokerDealerEmployee"
                rules={[
                  {
                    required: true,
                    message: "Please choose an answer!",
                  },
                ]}
              >
                <Select
                  placeholder="Select answer"
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ]}
                />
              </Form.Item>
            </div>

            <div className="field flex flex-col">
              <Label text="Are you a 5% shareholder, or policymaker of a publicly listed company?" required />

              <Form.Item
                name="isListedCompanyShareholder"
                rules={[
                  {
                    required: true,
                    message: "Please select an answer!",
                  },
                ]}
              >
                <Select
                  placeholder="Select answer"
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ]}
                />
              </Form.Item>
            </div>

            <div className="field flex flex-col">
              <Label text="Has the client worked in the financial sector during the past five years? (This includes, for example: working for capital market institutions, banks, finance companies, insurance companies)" required />

              <Form.Item
                name="hasWorkedInFinancialSector"
                rules={[
                  {
                    required: true,
                    message: "Please select an answer!",
                  },
                ]}
              >
                <Select
                  placeholder="Select answer"
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ]}
                />
              </Form.Item>
            </div>

            <div className="field flex flex-col">
              <Label text="Does the client have any other practical experience related to the financial sector?" required />
              <Form.Item
                name="hasOtherFinancialExperience"
                rules={[
                  {
                    required: true,
                    message: "Please select an answer!",
                  },
                ]}
              >
                <Select
                  placeholder="Select answer"
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ]}
                />
              </Form.Item>
            </div>

            <div className="fields flex flex-col">
              <Label text="Any other financial information about the client’s financial situation:" />
              <Form.Item name="sourceOfIncomeDetails">
                <TextArea
                  rows={4}
                  placeholder="Enter Additional Details on Source of Income"
                />
              </Form.Item>
            </div>
          </div>

          {/* Suitability Assessment Section */}
          <div className="suitability-assessment-display mt-6 mb-4">
            <div
              className="border rounded-xl p-6 shadow-sm"
              style={{ backgroundColor: "#e8eef5", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div
                    className="flex items-center justify-center w-12 h-12 rounded-full"
                    style={{ backgroundColor: "#002f6c" }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-white"
                    >
                      <path
                        d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4
                      className="text-lg font-semibold mb-1"
                      style={{ color: "#002f6c" }}
                    >
                      Client Suitability Assessment
                    </h4>
                    <p className="text-sm" style={{ color: "#777" }}>
                      Based on financial profile and investment experience
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div
                      className="text-2xl font-bold"
                      style={{ color: "#002f6c" }}
                    >
                      {getSuitabilityLevel(calculateSuitabilityScore())}
                    </div>
                    <div
                      className="text-xs uppercase tracking-wide"
                      style={{ color: "#777" }}
                    >
                      Score: {calculateSuitabilityScore()}/100
                    </div>
                  </div>
                  <div
                    className={`w-3 h-3 rounded-full`}
                    style={{
                      backgroundColor:
                        getSuitabilityLevel(calculateSuitabilityScore()) ===
                          "HIGH"
                          ? "#ef4444"
                          : getSuitabilityLevel(calculateSuitabilityScore()) ===
                            "MEDIUM"
                            ? "#f59e0b"
                            : "#10b981",
                    }}
                  ></div>
                </div>
              </div>

              <div
                className="mt-4 pt-4 border-t"
                style={{ borderColor: "#d9d9d9" }}
              >
                <div className="flex items-center justify-between text-sm mb-3">
                  <span style={{ color: "#777" }}>Suitability Level:</span>
                  <span className="font-medium" style={{ color: "#002f6c" }}>
                    {getSuitabilityLevel(calculateSuitabilityScore())}{" "}
                    SUITABILITY
                  </span>
                </div>

                <div className="mt-4">
                  <h5
                    className="text-sm font-semibold mb-2"
                    style={{ color: "#002f6c" }}
                  >
                    Suitable Products:
                  </h5>
                  <div className="flex flex-wrap gap-2">
                    {getSuitableProducts(calculateSuitabilityScore()).map(
                      (product, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: "#002f6c",
                            color: "white",
                          }}
                        >
                          {product}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Client Classification Section */}
          <div className="client-classification-display mt-6 mb-4">
            <div
              className="border rounded-xl p-6 shadow-sm"
              style={{ backgroundColor: "#e8eef5", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div
                    className="flex items-center justify-center w-12 h-12 rounded-full"
                    style={{ backgroundColor: "#002f6c" }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-white"
                    >
                      <path
                        d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H4C2.89 1 2 1.89 2 3V21C2 22.11 2.89 23 4 23H20C21.11 23 22 22.11 22 21V11H18C16.9 11 16 10.1 16 9Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4
                      className="text-lg font-semibold mb-1"
                      style={{ color: "#002f6c" }}
                    >
                      Client Classification
                    </h4>
                    <p className="text-sm" style={{ color: "#777" }}>
                      Based on employment, net worth, and certifications
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div
                      className="text-2xl font-bold"
                      style={{ color: "#002f6c" }}
                    >
                      {getClientClassification()}
                    </div>
                    <div
                      className="text-xs uppercase tracking-wide"
                      style={{ color: "#777" }}
                    >
                      {getClientClassification() === "QUALIFIED"
                        ? "Qualified Professional Client"
                        : "Retail Client"}
                    </div>
                  </div>
                  <div
                    className={`w-3 h-3 rounded-full`}
                    style={{
                      backgroundColor:
                        getClientClassification() === "QUALIFIED"
                          ? "#84a0c8"
                          : "#002f6c",
                    }}
                  ></div>
                </div>
              </div>
              <div
                className="mt-4 pt-4 border-t"
                style={{ borderColor: "#d9d9d9" }}
              >
                <div className="flex items-center justify-between text-sm mb-3">
                  <span style={{ color: "#777" }}>Classification Type:</span>
                  <span className="font-medium" style={{ color: "#002f6c" }}>
                    {getClientClassification() === "QUALIFIED"
                      ? "Qualified Professional Client"
                      : "Retail Client"}
                  </span>
                </div>

                <div className="mt-3">
                  <h5
                    className="text-sm font-semibold mb-2"
                    style={{ color: "#002f6c" }}
                  >
                    Classification Criteria:
                  </h5>
                  <div className="text-xs" style={{ color: "#777" }}>
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`w-2 h-2 rounded-full ${form.getFieldValue("wasBrokerDealerEmployee")
                          ? "bg-green-500"
                          : "bg-red-500"
                          }`}
                      ></span>
                      <span>
                        Employed by registered broker-dealer/securities exchange
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`w-2 h-2 rounded-full ${getQualifyingNetWorthAndCME1Certification() ? "bg-green-500"
                          : "bg-red-500"
                          }`}
                      ></span>
                      <span>Qualifying net worth (≥600K SAR) And A CME-1 certification</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${accountDetails?.accountInfo[0]
                          ?.hasAccreditedCertifications
                          ? "bg-green-500"
                          : "bg-red-500"
                          }`}
                      ></span>
                      <span>Has accredited professional certifications</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex w-full flex-wrap items-center justify-end mt-7  pt-5">
            <div className="flex justify-between items-center w-[70%] md:w-[48%]">
              <button
                onClick={() =>
                  navigate(
                    `/home/customers-list`
                  )
                }
                className="rounded-lg bg-white w-[48%] py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
              >
                Cancel
              </button>
              <button
                // disabled={!submitForm}
                className="disabled:opacity-50 disabled:cursor-not-allowed rounded-lg bg-blue-500 w-[48%] py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
              >
                Save and continue
              </button>
            </div>
          </div>
        </Form>
      )}
    </div>
  );
};

export default EditFinancial;
